import React, {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { Card, CardBody, Tooltip } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { Settlement } from 'common/src/utils/types';
import { formatMoneyUSD, formatNumber } from '../../utils/functions';
import { useTranslation } from '../../../../common/src/hooks/useTranslation';
import { SUMMARY_ROW_CLASS, TableExportRef, TH_BASE_CLASS } from '../../utils/table-styles';
import { exportTableToExcel } from '../../utils/export-to-excel';

interface Props {
  settlements: Settlement[];
}

type ColorCode = 'DD' | 'D' | 'L';
type SortDirection = 'ascending' | 'descending';

interface FobGroup {
  caliber: string;
  color: ColorCode;
  variety?: string;
  totalKilos: number;
  totalFob: number;
  detailCount: number;
}

interface MatrixMetric {
  value: number;
  totalKilos: number;
  totalFob: number;
  detailCount: number;
}

interface MatrixColumn {
  uid: string;
  name: string;
  color: ColorCode;
  caliber?: string;
  isAverage?: boolean;
}

interface MatrixColumnGroup {
  label: string;
  columns: MatrixColumn[];
}

const normalizeColor = (color?: string): ColorCode | null => {
  const value = color?.trim().toUpperCase();
  if (!value) return null;
  if (value === 'DD' || value === 'DARK DARK' || value === 'DARKDARK') {
    return 'DD';
  }
  if (value === 'D' || value === 'DARK') return 'D';
  if (value === 'L' || value === 'LIGHT') return 'L';
  return null;
};

const colorLabel = (color: ColorCode) => {
  if (color === 'DD') return 'DARK DARK';
  if (color === 'D') return 'DARK';
  return 'LIGHT';
};

const metricFromGroups = (groups: FobGroup[]): MatrixMetric | null => {
  const totalKilos = groups.reduce((sum, group) => sum + group.totalKilos, 0);
  if (totalKilos <= 0) return null;
  const totalFob = groups.reduce((sum, group) => sum + group.totalFob, 0);
  const detailCount = groups.reduce((sum, group) => sum + group.detailCount, 0);

  return {
    value: totalFob / totalKilos,
    totalKilos,
    totalFob,
    detailCount,
  };
};

const metricValue = (metric: MatrixMetric | null) =>
  metric ? formatMoneyUSD(metric.value, { decimals: 1 }) : '-';

const FOBAccumulatedCaliberColorTable = forwardRef<TableExportRef, Props>(
  ({ settlements }, ref) => {
    const { t } = useTranslation('settlements');
    const [sortState, setSortState] = useState<{
      column: string;
      direction: SortDirection;
    }>({
      column: '',
      direction: 'descending',
    });

    const calibersOrder = ['L', 'XL', 'J', '2J', '3J', '4J', '5J'];
    const totalLabel = t('Acumulado Total');

    const fobData = useMemo(() => {
      const totals: Record<string, FobGroup> = {};
      const byVariety: Record<string, FobGroup> = {};
      const varietyTotals: Record<string, FobGroup> = {};

      settlements.forEach((settlement) => {
        settlement.settlement_details?.forEach((detail) => {
          const color = normalizeColor(detail.color);
          const caliber = detail.caliber?.trim();
          const variety = detail.variety || t('Sin Variedad');

          if (!caliber || !color) return;

          const totalKey = `${caliber}-${color}`;
          const varietyKey = `${variety}-${caliber}-${color}`;

          if (!totals[totalKey]) {
            totals[totalKey] = {
              caliber,
              color,
              totalKilos: 0,
              totalFob: 0,
              detailCount: 0,
            };
          }

          if (!byVariety[varietyKey]) {
            byVariety[varietyKey] = {
              caliber,
              color,
              variety,
              totalKilos: 0,
              totalFob: 0,
              detailCount: 0,
            };
          }

          if (!varietyTotals[variety]) {
            varietyTotals[variety] = {
              caliber: '',
              color,
              variety,
              totalKilos: 0,
              totalFob: 0,
              detailCount: 0,
            };
          }

          const kilos = detail.kilos || 0;
          const fob = detail.usd_total_fob || 0;

          totals[totalKey].totalKilos += kilos;
          totals[totalKey].totalFob += fob;
          totals[totalKey].detailCount += 1;

          byVariety[varietyKey].totalKilos += kilos;
          byVariety[varietyKey].totalFob += fob;
          byVariety[varietyKey].detailCount += 1;

          varietyTotals[variety].totalKilos += kilos;
          varietyTotals[variety].totalFob += fob;
          varietyTotals[variety].detailCount += 1;
        });
      });

      return { totals, byVariety, varietyTotals };
    }, [settlements, t]);

    const columnGroups = useMemo<MatrixColumnGroup[]>(() => {
      return (['DD', 'D', 'L'] as ColorCode[])
        .map((color) => {
          const caliberColumns = calibersOrder
            .filter((caliber) => !!fobData.totals[`${caliber}-${color}`])
            .map((caliber) => ({
              uid: `${caliber}-${color}`,
              name: caliber,
              color,
              caliber,
            }));

          if (caliberColumns.length === 0) return null;

          return {
            label: colorLabel(color),
            columns: [
              ...caliberColumns,
              {
                uid: `AVG-${color}`,
                name: t('PROMEDIO'),
                color,
                isAverage: true,
              },
            ],
          };
        })
        .filter(Boolean) as MatrixColumnGroup[];
    }, [calibersOrder, fobData.totals, t]);

    const flatColumns = useMemo(
      () => columnGroups.flatMap((group) => group.columns),
      [columnGroups]
    );

    const getCellMetric = (
      variety: string,
      column: MatrixColumn
    ): MatrixMetric | null => {
      if (column.isAverage) {
        const source =
          variety === totalLabel ? fobData.totals : fobData.byVariety;
        const groups = Object.values(source).filter(
          (group) =>
            group.color === column.color &&
            (variety === totalLabel || group.variety === variety)
        );
        return metricFromGroups(groups);
      }

      if (!column.caliber) return null;

      const key =
        variety === totalLabel
          ? `${column.caliber}-${column.color}`
          : `${variety}-${column.caliber}-${column.color}`;

      const group =
        variety === totalLabel ? fobData.totals[key] : fobData.byVariety[key];
      return group ? metricFromGroups([group]) : null;
    };

    const rows = useMemo(
      () => [totalLabel, ...Object.keys(fobData.varietyTotals).sort()],
      [fobData.varietyTotals, totalLabel]
    );

    const sortedRows = useMemo(() => {
      const [summaryRow, ...dataRows] = rows;
      if (!sortState.column) return rows;

      if (sortState.column === 'variety') {
        const sorted = [...dataRows].sort((a, b) =>
          sortState.direction === 'ascending'
            ? a.localeCompare(b)
            : b.localeCompare(a)
        );
        return [summaryRow, ...sorted];
      }

      const column = flatColumns.find((col) => col.uid === sortState.column);
      if (!column) return rows;

      const sorted = [...dataRows].sort((a, b) => {
        const metricA = getCellMetric(a, column);
        const metricB = getCellMetric(b, column);

        if (!metricA && !metricB) return a.localeCompare(b);
        if (!metricA) return 1;
        if (!metricB) return -1;

        return sortState.direction === 'ascending'
          ? metricA.value - metricB.value
          : metricB.value - metricA.value;
      });

      return [summaryRow, ...sorted];
    }, [flatColumns, rows, sortState]);

    const heatRange = useMemo(() => {
      const values = rows.flatMap((row) =>
        flatColumns
          .map((column) => getCellMetric(row, column)?.value)
          .filter((value): value is number => typeof value === 'number')
      );

      return {
        min: Math.min(...values),
        max: Math.max(...values),
      };
    }, [flatColumns, rows, fobData]);

    const getHeatStyle = (metric: MatrixMetric | null): React.CSSProperties => {
      if (!metric || !isFinite(heatRange.min) || !isFinite(heatRange.max)) {
        return {};
      }

      const range = heatRange.max - heatRange.min;
      const ratio = range > 0 ? (metric.value - heatRange.min) / range : 0.5;
      const alpha = 0.08 + ratio * 0.28;

      return {
        backgroundColor: `rgba(16, 185, 129, ${alpha})`,
      };
    };

    const handleSort = (column: string) => {
      setSortState((current) => ({
        column,
        direction:
          current.column === column && current.direction === 'descending'
            ? 'ascending'
            : 'descending',
      }));
    };

    const SortIndicator = ({ column }: { column: string }) => {
      if (sortState.column !== column) {
        return (
          <Icon
            icon='tabler:chevron-down'
            className='inline ml-1 opacity-30'
            width={10}
          />
        );
      }
      return sortState.direction === 'ascending' ? (
        <Icon icon='tabler:chevron-up' className='inline ml-1' width={10} />
      ) : (
        <Icon icon='tabler:chevron-down' className='inline ml-1' width={10} />
      );
    };

    const renderTooltip = (metric: MatrixMetric) => (
      <div className='space-y-1 text-xs'>
        <div>
          {t('FOB promedio')}: {formatMoneyUSD(metric.value, { decimals: 2 })}
        </div>
        <div>
          {t('Kilos')}: {formatNumber(metric.totalKilos, { decimals: 0 })}
        </div>
        <div>
          {t('FOB total')}: {formatMoneyUSD(metric.totalFob, { decimals: 0 })}
        </div>
        <div>
          {t('Detalles')}: {formatNumber(metric.detailCount, { decimals: 0 })}
        </div>
      </div>
    );

    const renderMetricCell = (
      row: string,
      column: MatrixColumn,
      isSummary: boolean
    ) => {
      const metric = getCellMetric(row, column);

      if (!metric) {
        return (
          <td key={column.uid} className='py-2 px-3 text-right text-default-300'>
            -
          </td>
        );
      }

      return (
        <td key={column.uid} className='py-1.5 px-2 text-right'>
          <Tooltip content={renderTooltip(metric)} placement='top'>
            <span
              className={`inline-flex w-full min-w-[76px] justify-end rounded-md px-2 py-1 tabular-nums ${
                isSummary ? 'font-semibold' : ''
              }`}
              style={getHeatStyle(metric)}
            >
              {metricValue(metric)}
            </span>
          </Tooltip>
        </td>
      );
    };

    const handleExport = () => {
      const headers = [
        t('Variedad'),
        ...columnGroups.flatMap((group) =>
          group.columns.map((column) => `${group.label} ${column.name}`)
        ),
      ];

      const exportRows = rows.map((row) => [
        row,
        ...flatColumns.map((column) => metricValue(getCellMetric(row, column))),
      ]);

      exportTableToExcel({
        fileName: 'fob-acumulado-calibre-color',
        sheetName: 'FOB Calibre Color',
        headers,
        rows: exportRows,
      });
    };

    useImperativeHandle(ref, () => ({
      exportToExcel: handleExport,
    }));

    return (
      <Card shadow='sm' className='w-full'>
        <CardBody className='p-0'>
          <div className='max-h-[620px] overflow-auto'>
            <table
              className='w-full border-separate border-spacing-0 text-sm'
              style={{ minWidth: `${Math.max(900, 120 + flatColumns.length * 86)}px` }}
            >
              <thead className='sticky top-0 z-20'>
                <tr>
                  <th
                    rowSpan={2}
                    className={`${TH_BASE_CLASS} sticky left-0 z-30 bg-default-100 text-left`}
                    onClick={() => handleSort('variety')}
                  >
                    {t('VARIEDAD')}
                    <SortIndicator column='variety' />
                  </th>
                  {columnGroups.map((group) => (
                    <th
                      key={group.label}
                      colSpan={group.columns.length}
                      className='border-b border-l border-default-200 bg-default-100 py-1.5 text-center text-[11px] font-semibold text-default-500'
                    >
                      {group.label}
                    </th>
                  ))}
                </tr>
                <tr>
                  {columnGroups.flatMap((group) =>
                    group.columns.map((column, index) => (
                      <th
                        key={column.uid}
                        className={`${TH_BASE_CLASS} bg-default-100 text-right ${
                          index === 0 ? 'border-l border-default-200' : ''
                        }`}
                        onClick={() => handleSort(column.uid)}
                      >
                        {column.name}
                        <SortIndicator column={column.uid} />
                      </th>
                    ))
                  )}
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((row) => {
                  const isSummary = row === totalLabel;
                  return (
                    <tr
                      key={row}
                      className={
                        isSummary
                          ? SUMMARY_ROW_CLASS
                          : 'border-b border-default-100 hover:bg-default-50'
                      }
                    >
                      <td className='sticky left-0 z-10 bg-background py-2 px-3 font-medium'>
                        {row}
                      </td>
                      {flatColumns.map((column) =>
                        renderMetricCell(row, column, isSummary)
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>
    );
  }
);

FOBAccumulatedCaliberColorTable.displayName =
  'FOBAccumulatedCaliberColorTable';

export default React.memo(FOBAccumulatedCaliberColorTable);
